/**
 * @file   src\components\Inventory\EditInventory\InventoryFileUpload.js
 * @brief  This file is responsible for the view of inventory image upload section.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */

import React, { useState, useEffect, useRef } from "react";
import { uploadToS3 } from "../../../helpers/S3/uploadToS3";
import { getPreSignedUrls } from "../../../services/fileUploadService";
import { getS3Details, deleteFile } from "../../../services/fileUploadService";
import heic2any from "heic2any";
import {
  getLocalStorage,
  getFileType,
  sortItems,
  reSizeImage,
  setLocalStorage,
} from "../../../helpers/common";
import Loader from "../../Common/Loader";
import DropZoneFileUpload from "../../Common/DropZoneFileUpload";
import { convertToNumber } from "../../../helpers/validations";
import inValidImage from "../../../images/invalid-image-placeholder.png";
import {
  MAX_FILE_SIZE,
  MAX_FILES,
  MIN_HEIGHT,
  MIN_WIDHT,
  supportedImageFormats,
} from "../../../constants/common";

const InventoryFileUpload = (props) => {
  const {
    handleFormValues,
    photos,
    s3Imagefolder,
    isFolderCreated,
    currentPhotoCount,
    sessionKey,
  } = props;

  // Initialize component state variables and constants.
  const [isUploading, setIsUploading] = useState(false);
  const [imageCount, setImageCount] = useState(0);
  const [invalidImgCount, setInvalidImgCount] = useState(0);
  const [uploadingPhotosCount, setPhotosCount] = useState(0);
  const baseFolderPath = "/InventoryImages/";
  const dragItem = useRef();
  const dragOverItem = useRef();
  const s3BucketBaseUrl = process.env.REACT_APP_S3_BASE_URL;
  const isCoverPositionArray = [1, 2, 3];

  // To get the file name
  const getPropertyValue = (object, key) => {
    return _.get(object, key, "");
  };

  // On uploading the file
  const uploadFile = async (uploadedFiles) => {
    setIsUploading(true);
    setInvalidImgCount(0);
    setPhotosCount(0);
    let updatedArray = photos;
    let filesArray = [];
    let urlUniqueId = 1;
    uploadedFiles.map((file, index) => {
      const imageId = (imageCount !== 0 ? imageCount + 1 : 1) + index;
      const fileName = getPropertyValue(file, "name")?.replace(/ /g, "-");
      const imagePath =
        baseFolderPath + getFolder() + "/" + imageId + "_" + fileName;
      filesArray.push({ id: urlUniqueId, path: imagePath, file: file });
      urlUniqueId++;
    });
    setPhotosCount(filesArray?.length);
    const filePathArray = filesArray.map(({ file, ...rest }) => rest); // Removing file from the array for passing into API call for getting PresignedUrls.

    try {
      const folderName = getFolder();
      const reqPayload = !folderName
        ? { folder: baseFolderPath + getFolder() + "/", keys: filePathArray }
        : { keys: filePathArray }; // Creating s3 folder for first image upload.
      const response = await getPreSignedUrls(reqPayload);
      if (response && _.get(response, "errorCode") === 0) {
        const preSignedUrlsArray = _.get(response, "result", []);
        let count = imageCount !== 0 ? imageCount + 1 : 1;
        filesArray.forEach(async (fa, index) => {
          let file = _.get(fa, "file", "");

          const fileType = file.type;
          const isValidType = _.includes(supportedImageFormats, fileType);

          if (isValidType) {
            try {
              if (fileType === "image/heic") {
                // Convert HEIC to JPEG
                const convertedBlob = await heic2any({
                  blob: file,
                  quality: 1,
                });

                // Create a new File with the converted data
                const convertedFile = new File(
                  [convertedBlob],
                  `${file.name}.jpeg`,
                  {
                    type: "image/jpeg",
                  }
                );

                // Use the converted file for further processing or upload
                file = convertedFile;
              }
            } catch { }

            const image = new Image();
            image.src = URL.createObjectURL(file); // This is for getting image width and height
            image.addEventListener("load", async () => {
              const fileSize = getPropertyValue(file, "size");
              const fileName = getPropertyValue(file, "name")?.replace(
                / /g,
                "-"
              );
              const fileId = getPropertyValue(fa, "id");
              const selectedFileType = getFileType(fileName);
              const filteredArray = preSignedUrlsArray.filter(function (url) {
                return url.id === fileId;
              });

              const isValid = await isValidImage(image.width, image.height);
              const currentCount = count + index;
              const preSignedS3Url = filteredArray
                ? _.get(filteredArray[0], "presignedUrl", "")
                : "";

              if (isValid) {
                const resizedImage =
                  fileSize > MAX_FILE_SIZE
                    ? await reSizeImage({
                      file: file,
                      width: MIN_WIDHT,
                      height: MIN_HEIGHT,
                      fileType: selectedFileType,
                    })
                    : file;

                const isCover = _.includes(isCoverPositionArray, currentCount);

                if (currentCount > MAX_FILES) {
                  setIsUploading(false);
                  alert("Upload limit 20 exceeds.");
                } else {
                  try {
                    const res = await uploadToS3(preSignedS3Url, resizedImage);
                    const status = _.get(res, "status", "");
                    const fileFullUrl = _.get(res, "config.url", "");
                    const fileS3Url = fileFullUrl
                      ? fileFullUrl.split("?")[0]
                      : ""; // For excluding Aws credentials from the url and access s3 image Url.

                    if (status === 200) {
                      if (fileS3Url) {
                        updatedArray.push({
                          image: fileS3Url,
                          isCover: isCover,
                          order: currentCount,
                          IsValid: 1,
                        });
                        setImageCount((prevState) => prevState + 1);
                        handleFormValues({
                          photos: updatedArray,
                        });
                      }
                    } else {
                      console.log(`---#####---Error---#####---`);
                    }
                  } catch (error) {
                    console.error(error);
                  }
                }
              } else {
                setInvalidImgCount((prevState) => prevState + 1);
              }
            });
          } else {
            setInvalidImgCount((prevState) => prevState + 1);
          }
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // To get the folder name from session
  const getFolder = () => {
    let folderName = s3Imagefolder;
    if (!folderName) {
      const inventoryDetails = getLocalStorage(sessionKey);
      folderName = _.get(inventoryDetails, "s3ImageFolder", "");
    }
    return folderName;
  };

  // To check the image valid or not
  const isValidImage = async (imgWidth, imgHeight) => {
    if (imgWidth >= MIN_WIDHT && imgHeight >= MIN_HEIGHT) {
      return true;
    }
    return false;
  };

  // To get the folder name for image upload if folder name not exist in session
  const getS3FolderName = async () => {
    const folderName = getFolder();
    const requestPayload = { folderName: folderName };
    await getS3Details(requestPayload)
      .then((response) => {
        if (response.errorCode === 0 && _.get(response, "result", null)) {
          const resResult = _.get(response, "result", null);
          const folderName = _.get(resResult, "folderName", "");
          handleFormValues({
            s3ImageFolder: folderName,
          });
        } else {
          console.log("Something went wrong");
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  // Check to get the folder name if it not exist in session checking and set image count
  useEffect(() => {
    setImageCount(photos.length);
    const isFolderExist = getFolder();
    if (!isFolderExist) {
      getS3FolderName();
    }
  }, []);

  // invalidImgCount and uploadingPhotosCount state change side effect handling.
  useEffect(() => {
    if (uploadingPhotosCount === 1 && invalidImgCount === 1) {
      setIsUploading(false);
      alert(
        "Could not complete the process. Please upload valid files of minimum 700x400 pixels and jpg, jpeg, png or heic file type."
      );
    } else if (uploadingPhotosCount > 1 && invalidImgCount === 1) {
      alert(
        `Failed to upload ${invalidImgCount} file. Please upload valid files only of minimum 700x400 pixels and jpg, jpeg, png or heic file type.`
      );
    } else if (invalidImgCount > 1) {
      alert(
        `Failed to upload ${invalidImgCount} files. Please upload valid files only of minimum 700x400 pixels and jpg, jpeg, png or heic file type.`
      );
    }
  }, [invalidImgCount, uploadingPhotosCount]);

  // photos count state change side effect handling.
  useEffect(() => {
    setIsUploading(false);
  }, [photos.length]);

  // On Drag start
  const dragStart = (position) => {
    dragItem.current = position;
  };
  // On Drag enter
  const dragEnter = (position) => {
    dragOverItem.current = position;
  };

  // on drop the image
  const drop = (order) => {
    const dragItemOrder = order;
    const dragOverItemOrder = convertToNumber(dragOverItem.current) + 1;
    if (dragItem.current !== dragOverItem.current) {
      const reOrderedArray = reArrangeImage(dragItemOrder, dragOverItemOrder);
      handleFormValues({ photos: reOrderedArray });
    }
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const deleteFileFromS3 = async (imagePath, order) => {
    const inventoryDetails = getLocalStorage(sessionKey);
    const inventoryId = _.get(inventoryDetails, "inventoryId", "");
    const requestPayload = { inventoryId: inventoryId, keys: [imagePath] };

    await deleteFile(requestPayload)
      .then((response) => {
        if (response.errorCode === 0) {
          const reOrderedPhotosArray = reOrderingAfterDelete(order);
          handleFormValues({
            photos: reOrderedPhotosArray,
          });
          setLocalStorage("PhotosAfterDelete", reOrderedPhotosArray);
        } else {
          console.log("Something went wrong");
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  // To reorder the image order after delete the image
  const reOrderingAfterDelete = (updatedImageOrder) => {
    const photosCopy = [...photos];
    const filterPhotosArray = photosCopy.filter(function (imgArray) {
      return imgArray.order !== updatedImageOrder;
    });
    setImageCount(filterPhotosArray?.length);

    const reOrderedPhotos = filterPhotosArray.map((img) => {
      const currentOrder = convertToNumber(_.get(img, "order", ""));
      const isValid = _.get(img, "IsValid", null);
      if (currentOrder > convertToNumber(updatedImageOrder)) {
        const updatedOrder = convertToNumber(currentOrder) - 1;
        const isCover = _.includes(isCoverPositionArray, updatedOrder);
        return {
          image: _.get(img, "image", ""),
          order: updatedOrder,
          isCover: isCover,
          IsValid: isValid,
        };
      } else {
        return {
          image: _.get(img, "image", ""),
          order: convertToNumber(currentOrder),
          isCover: _.get(img, "isCover", ""),
          IsValid: isValid,
        };
      }
    });
    return reOrderedPhotos;
  };

  // on rearranging the image
  const reArrangeImage = (dragItemOrder, dragOverItemOrder) => {
    const photosCopy = [...photos];
    if (dragItemOrder < dragOverItemOrder) {
      const reOrderedPhotos = photosCopy.map((img) => {
        const currentOrder = convertToNumber(_.get(img, "order", ""));
        const isValid = _.get(img, "IsValid", null);
        if (currentOrder === convertToNumber(dragItemOrder)) {
          const isCover = _.includes(isCoverPositionArray, dragOverItemOrder);

          return {
            image: _.get(img, "image", ""),
            order: dragOverItemOrder,
            isCover: isCover,
            IsValid: isValid,
          };
        } else if (
          currentOrder <= convertToNumber(dragOverItemOrder) &&
          currentOrder > convertToNumber(dragItemOrder)
        ) {
          const updatedOrder = currentOrder - 1;
          return {
            image: _.get(img, "image", ""),
            order: updatedOrder,
            isCover: _.includes(isCoverPositionArray, updatedOrder),
            IsValid: isValid,
          };
        } else {
          return {
            image: _.get(img, "image", ""),
            order: convertToNumber(_.get(img, "order", "")),
            isCover: _.get(img, "isCover", ""),
            IsValid: isValid,
          };
        }
      });

      return reOrderedPhotos;
    }
    if (dragItemOrder > dragOverItemOrder) {
      const reOrderedPhotos = photosCopy.map((img) => {
        const currentOrder = convertToNumber(_.get(img, "order", ""));
        const isValid = _.get(img, "IsValid", null);
        if (currentOrder === convertToNumber(dragItemOrder)) {
          const isCover = _.includes(
            isCoverPositionArray,
            convertToNumber(dragOverItemOrder)
          );
          return {
            image: _.get(img, "image", ""),
            order: convertToNumber(dragOverItemOrder),
            isCover: isCover,
            IsValid: isValid,
          };
        } else if (
          currentOrder <= convertToNumber(dragItemOrder) &&
          currentOrder >= convertToNumber(dragOverItemOrder)
        ) {
          const updatedOrder = currentOrder + 1;
          return {
            image: _.get(img, "image", ""),
            order: updatedOrder,
            isCover: _.includes(isCoverPositionArray, updatedOrder),
            IsValid: isValid,
          };
        } else {
          return {
            image: _.get(img, "image", ""),
            order: convertToNumber(_.get(img, "order", "")),
            isCover: _.get(img, "isCover", ""),
            IsValid: isValid,
          };
        }
      });
      return reOrderedPhotos;
    }
  };

  return (
    <>
      <div className="center-content-sec">
        {imageCount === 0 && (
          <div className="file-upload-sec clearfix">
            <div className="file-upload-outer">
              <h6>Add photos (0/20)</h6>
              <div
                className={
                  isUploading
                    ? "file-upload-inner mt-2 position-relative"
                    : "file-upload-inner mt-2"
                }
              >
                {isUploading ? <Loader /> : ""}

                <DropZoneFileUpload
                  uploadFile={uploadFile}
                  uploadIconExist={true}
                  uploadTitle="Drag or upload photos here"
                  uploadDesc="We recommend adding at least 5"
                />
              </div>
            </div>
            <div className="file-upload-text">
              Here are some tips for taking photos of your property.{" "}
              <span className="blue-info-icon">
                <svg
                  width="14"
                  height="13"
                  viewBox="0 0 14 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7 1C3.96243 1 1.5 3.46243 1.5 6.5C1.5 9.53757 3.96243 12 7 12C10.0376 12 12.5 9.53757 12.5 6.5C12.5 3.46243 10.0376 1 7 1ZM0.5 6.5C0.5 2.91015 3.41015 0 7 0C10.5899 0 13.5 2.91015 13.5 6.5C13.5 10.0899 10.5899 13 7 13C3.41015 13 0.5 10.0899 0.5 6.5Z"
                    fill="#1C52B0"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6 6C6 5.72386 6.22386 5.5 6.5 5.5H7C7.27614 5.5 7.5 5.72386 7.5 6V9C7.77614 9 8 9.22386 8 9.5C8 9.77614 7.77614 10 7.5 10H7C6.72386 10 6.5 9.77614 6.5 9.5V6.5C6.22386 6.5 6 6.27614 6 6Z"
                    fill="#1C52B0"
                  />
                  <path
                    d="M6.875 4.5C7.28921 4.5 7.625 4.16421 7.625 3.75C7.625 3.33579 7.28921 3 6.875 3C6.46079 3 6.125 3.33579 6.125 3.75C6.125 4.16421 6.46079 4.5 6.875 4.5Z"
                    fill="#1C52B0"
                  />
                </svg>
              </span>{" "}
            </div>
          </div>
        )}
        {imageCount > 0 && (
          <div className="file-display-sec clearfix">
            <div className="file-upload-outer">
              <h6>Add photos ({imageCount}/20)</h6>
              <p className="mt-2">
                Click and drag photos to rearrange their order. The first three
                photos will be on the property preview of your introduction. The
                rest of your photos will be seen after they accept your
                introduction.{" "}
              </p>
              <div className="uploaded-files-wrap">
                <section
                  className={
                    isUploading
                      ? "uploaded-file-list w-100 h-100 position-relative"
                      : "uploaded-file-list w-100 h-100 "
                  }
                >
                  {isUploading ? <Loader /> : ""}
                  {photos.sort(sortItems("asc", "order")).map((img, index) => {
                    const order = _.get(img, "order", "");
                    const isCover =
                      _.get(img, "isCover", "") === 0 ||
                        _.get(img, "isCover", "") === "0"
                        ? null
                        : _.get(img, "isCover", "");
                    const isValid = _.get(img, "IsValid", null);
                    const image =
                      isValid != 0 ? _.get(img, "image", "") : inValidImage;

                    return (
                      <div
                        className="uploaded-file-item"
                        draggable="true"
                        onDragStart={(e) => dragStart(index)}
                        onDragEnter={(e) => dragEnter(index)}
                        onDragEnd={() => drop(order)}
                        key={index}
                      >
                        {isCover && (
                          <div className="upload-cover-btn">Cover</div>
                        )}

                        <button
                          className="btn upload-delete-btn"
                          onClick={() =>
                            deleteFileFromS3(
                              _.get(img, "image", "")?.replace(
                                s3BucketBaseUrl,
                                ""
                              ),
                              order
                            )
                          }
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8 1.3335C6.83056 1.3335 5.8568 2.20731 5.69336 3.3335H3.41276C3.38437 3.32864 3.35562 3.32624 3.32682 3.32633C3.30194 3.32687 3.27713 3.32927 3.2526 3.3335H2.16667C2.10041 3.33256 2.03463 3.3448 1.97315 3.36951C1.91166 3.39421 1.8557 3.43089 1.80852 3.47742C1.76133 3.52394 1.72386 3.57937 1.69829 3.6405C1.67272 3.70163 1.65955 3.76723 1.65955 3.8335C1.65955 3.89976 1.67272 3.96536 1.69829 4.02649C1.72386 4.08762 1.76133 4.14305 1.80852 4.18958C1.8557 4.2361 1.91166 4.27278 1.97315 4.29749C2.03463 4.32219 2.10041 4.33443 2.16667 4.3335H2.87956L3.71875 13.0099C3.80911 13.9455 4.60393 14.6668 5.54362 14.6668H10.4557C11.3955 14.6668 12.1903 13.9456 12.2806 13.0099L13.1204 4.3335H13.8333C13.8996 4.33443 13.9654 4.32219 14.0269 4.29749C14.0883 4.27278 14.1443 4.2361 14.1915 4.18958C14.2387 4.14305 14.2761 4.08762 14.3017 4.02649C14.3273 3.96536 14.3405 3.89976 14.3405 3.8335C14.3405 3.76723 14.3273 3.70163 14.3017 3.6405C14.2761 3.57937 14.2387 3.52394 14.1915 3.47742C14.1443 3.43089 14.0883 3.39421 14.0269 3.36951C13.9654 3.3448 13.8996 3.33256 13.8333 3.3335H12.748C12.695 3.32489 12.6409 3.32489 12.5879 3.3335H10.3066C10.1432 2.20731 9.16944 1.3335 8 1.3335ZM8 2.3335C8.62639 2.3335 9.14026 2.75622 9.28711 3.3335H6.71289C6.85975 2.75622 7.37362 2.3335 8 2.3335ZM3.88346 4.3335H12.1159L11.2852 12.9136C11.2435 13.3453 10.8893 13.6668 10.4557 13.6668H5.54362C5.11065 13.6668 4.75583 13.3447 4.71419 12.9136L3.88346 4.3335ZM6.82552 5.993C6.69303 5.99507 6.56677 6.04965 6.47449 6.14474C6.3822 6.23983 6.33143 6.36767 6.33333 6.50016V11.5002C6.3324 11.5664 6.34464 11.6322 6.36934 11.6937C6.39405 11.7552 6.43073 11.8111 6.47725 11.8583C6.52378 11.9055 6.57921 11.943 6.64034 11.9685C6.70147 11.9941 6.76707 12.0073 6.83333 12.0073C6.8996 12.0073 6.9652 11.9941 7.02633 11.9685C7.08746 11.943 7.14289 11.9055 7.18941 11.8583C7.23594 11.8111 7.27262 11.7552 7.29732 11.6937C7.32203 11.6322 7.33427 11.5664 7.33333 11.5002V6.50016C7.33429 6.43324 7.32181 6.36681 7.29662 6.3048C7.27143 6.24279 7.23405 6.18647 7.1867 6.13917C7.13934 6.09188 7.08297 6.05457 7.02093 6.02946C6.95889 6.00435 6.89244 5.99196 6.82552 5.993ZM9.15885 5.993C9.02636 5.99507 8.9001 6.04965 8.80782 6.14474C8.71553 6.23983 8.66477 6.36767 8.66667 6.50016V11.5002C8.66573 11.5664 8.67797 11.6322 8.70268 11.6937C8.72739 11.7552 8.76407 11.8111 8.81059 11.8583C8.85711 11.9055 8.91255 11.943 8.97367 11.9685C9.0348 11.9941 9.1004 12.0073 9.16667 12.0073C9.23293 12.0073 9.29853 11.9941 9.35966 11.9685C9.42079 11.943 9.47622 11.9055 9.52275 11.8583C9.56927 11.8111 9.60595 11.7552 9.63066 11.6937C9.65536 11.6322 9.6676 11.5664 9.66667 11.5002V6.50016C9.66763 6.43324 9.65514 6.36681 9.62995 6.3048C9.60477 6.24279 9.56739 6.18647 9.52003 6.13917C9.47267 6.09188 9.41631 6.05457 9.35427 6.02946C9.29222 6.00435 9.22578 5.99196 9.15885 5.993Z"
                              fill="#333A48"
                            />
                          </svg>
                        </button>
                        <img
                          className="uploaded-file-img w-100"
                          src={image}
                          alt={image}
                        />
                      </div>
                    );
                  })}
                  <div className="uploaded-photos-btnwrap-outer">
                    <div className="uploaded-photos-btnwrap-inner">
                      <DropZoneFileUpload uploadFile={uploadFile} />
                    </div>
                  </div>
                </section>
              </div>
            </div>
            <div className="file-upload-text">
              Here are some tips for taking photos of your property.{" "}
              <span className="blue-info-icon">
                <svg
                  width="14"
                  height="13"
                  viewBox="0 0 14 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7 1C3.96243 1 1.5 3.46243 1.5 6.5C1.5 9.53757 3.96243 12 7 12C10.0376 12 12.5 9.53757 12.5 6.5C12.5 3.46243 10.0376 1 7 1ZM0.5 6.5C0.5 2.91015 3.41015 0 7 0C10.5899 0 13.5 2.91015 13.5 6.5C13.5 10.0899 10.5899 13 7 13C3.41015 13 0.5 10.0899 0.5 6.5Z"
                    fill="#1C52B0"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6 6C6 5.72386 6.22386 5.5 6.5 5.5H7C7.27614 5.5 7.5 5.72386 7.5 6V9C7.77614 9 8 9.22386 8 9.5C8 9.77614 7.77614 10 7.5 10H7C6.72386 10 6.5 9.77614 6.5 9.5V6.5C6.22386 6.5 6 6.27614 6 6Z"
                    fill="#1C52B0"
                  />
                  <path
                    d="M6.875 4.5C7.28921 4.5 7.625 4.16421 7.625 3.75C7.625 3.33579 7.28921 3 6.875 3C6.46079 3 6.125 3.33579 6.125 3.75C6.125 4.16421 6.46079 4.5 6.875 4.5Z"
                    fill="#1C52B0"
                  />
                </svg>
              </span>{" "}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default InventoryFileUpload;
